import {NumberFormat} from "core/format/NumberFormat";
import {useSelector} from 'react-redux'
import {StoreState} from "core/redux/StoreState";
import {TranslationKey} from "enum/TranslationKey";
import {TranslationParam} from "enum/TranslationParam";
import Platform from "platform/Platform";
import Translations from "platform/translation/Translations";
import Parameter from "platform/util/Parameter";
import Utils from "platform/util/Utils";
import {AccountOption} from "protocol/account/AccountOption";
import {LoginAccountType} from "protocol/auth/LoginAccountType";
import {TSMap} from "typescript-map";
import {AccountConfigType} from "enum/AccountConfigType";
import {Preference} from "core/util/Preference";
import {StorageKey} from "enum/StorageKey";
import {InsertClientPreference, SetClientPreference } from "core/redux/settings/SettingsReduxActions";
import {Configuration} from "core/configuration/Configuration";

interface AccountDetailRowMeta {

    title: TranslationKey;
    description: string;
    value: string;
    clazz?: string;
    showIcon?: boolean;
    hidden?: boolean;
    percentage?: number;
    quickLook?: boolean;
    Icon?: React.ComponentType<any>;
}

interface AccountDetailsConfig {

    title: TranslationKey;
    quickLook: boolean;
}

const defaultAccountConfig = (): TSMap<AccountConfigType, AccountDetailsConfig[]> => {
    const Config: TSMap<AccountConfigType, AccountDetailsConfig[]> = new TSMap<AccountConfigType, AccountDetailsConfig[]>();
    Config.set(AccountConfigType.Mobile, getDefaultAccountConfig(true));
    Config.set(AccountConfigType.Desktop, getDefaultAccountConfig(false));

    return Config;
}

const getDefaultAccountConfig = (isMobile: boolean): AccountDetailsConfig[] => {

    const configuration: Configuration = Platform.config<Configuration>();

    return [
        {
            title: TranslationKey.headerUnrealizedProfit,
            quickLook: true,
        },
        {
            title: TranslationKey.headerMarginLevel,
            quickLook: true,
        },
        {
            title: TranslationKey.headerActualEquity,
            quickLook: !isMobile,
        },
        {
            title: TranslationKey.headerAccountEquity,
            quickLook: true,
        },
        {
            title: TranslationKey.headerBalance,
            quickLook: !isMobile || configuration?.homeShowBalance,
        },
        {
            title: TranslationKey.headerAvailableMargin,
            quickLook: !isMobile || !configuration?.homeShowBalance,
        },
        {
            title: TranslationKey.headerExposure,
            quickLook: false,
        },
        {
            title: TranslationKey.headerAvailableLine,
            quickLook: false,
        },
        {
            title: TranslationKey.headerRealizedProfit,
            quickLook: false,
        },
        {
            title: TranslationKey.headerUsedMargin,
            quickLook: false,
        },
        {
            title: TranslationKey.headerMaintenanceMargin,
            quickLook: false,
        },
        {
            title: TranslationKey.headerWithdrawableEquity,
            quickLook: false,
        }
    ]
}

const getAccountDetailsMeta = (isFinancialSwiper?: boolean, accountDetailsConfig?: AccountDetailsConfig[]): AccountDetailRowMeta[] => {
    const {
        UsableMargin = 0,
        AvailableLine = 0,
        UnrealizedPL = 0,
        RealizedPL = 0,
        UsedMargin = 0,
        Exposure = 0,
        MarginPercentage = 0,
        MarginCallPercentage = 0,
        WithdrawableEquity = 0,
        Balance = 0,
        Currency,
        Equity = 0,
        MaintenanceMargin,
        ShownMenuItems,
        ActualEquity,
        MaxExposure = 0,
    } = useSelector((state: StoreState) => state.account);

    const {loginAccountType} = Platform.reduxState<StoreState>().auth;
    const {dynamicConfiguration} = Platform.reduxState<StoreState>().app
    const IsInvestAccount: boolean = loginAccountType === LoginAccountType.Invest;
    const isQuantumAccount: boolean = loginAccountType === LoginAccountType.Quantum;
    const WithdrawableEquityEnabled: boolean = (ShownMenuItems & AccountOption.WithdrawableEquity) > 0;

    const getDetailVisibility = (trKey: TranslationKey): boolean => {
        const item: AccountDetailsConfig = accountDetailsConfig.find(item => item.title === trKey);
        return item?.quickLook;
    }

    const UnrealizedProfit: AccountDetailRowMeta = {
        title: TranslationKey.headerUnrealizedProfit,
        description: Translations.text(TranslationKey.headerDescriptionUnrealizedProfit),
        value: (UnrealizedPL > 0 ? "+" : "") + NumberFormat.formatMoney(UnrealizedPL, Currency, 2),
        clazz: UnrealizedPL > 0 ? "up" : UnrealizedPL < 0 ? "down" : "",
        hidden: false,
        quickLook: Utils.isArrayNotEmpty(accountDetailsConfig) ? getDetailVisibility(TranslationKey.headerUnrealizedProfit) : null
    };
    const AvailableMargin: AccountDetailRowMeta = {
        title: TranslationKey.headerAvailableMargin,
        description: Translations.text(TranslationKey.headerDescriptionAvailableMargin),
        value: `${NumberFormat.formatMoney(UsableMargin, Currency, 2)}`,
        hidden: isQuantumAccount,
        quickLook: Utils.isArrayNotEmpty(accountDetailsConfig) ? getDetailVisibility(TranslationKey.headerAvailableMargin) : null
    };
    const MarginLevel: AccountDetailRowMeta = {
        title: TranslationKey.headerMarginLevel,
        description: Translations.text(TranslationKey.headerDescriptionMarginLevel, Parameter.Of(TranslationParam.value, NumberFormat.formatPercent(MarginCallPercentage || 1))),
        value: NumberFormat.formatPercent(MarginPercentage),
        hidden: IsInvestAccount || isQuantumAccount,
        quickLook: Utils.isArrayNotEmpty(accountDetailsConfig) ? getDetailVisibility(TranslationKey.headerMarginLevel) : null
    };
    const AccountEquity: AccountDetailRowMeta = {
        title: isQuantumAccount ? TranslationKey.headerActualEquity : TranslationKey.headerAccountEquity,
        description: Translations.text(TranslationKey.headerDescriptionAccountEquity),
        value: `${NumberFormat.formatMoney(isQuantumAccount ? ActualEquity : Equity, Currency, 2)}`,
        showIcon: true,
        hidden: false,
        quickLook: Utils.isArrayNotEmpty(accountDetailsConfig) ? getDetailVisibility(isQuantumAccount ? TranslationKey.headerActualEquity : TranslationKey.headerAccountEquity) : null
    };
    const AccountExposure: AccountDetailRowMeta = {
        title: TranslationKey.headerExposure,
        description: Translations.text(TranslationKey.headerDescriptionExposure),
        value: `${isQuantumAccount ? Translations.text(TranslationKey.exposureOutOf, Parameter.Of(TranslationParam.value, NumberFormat.formatMoney(Utils.nullTo0(Exposure), Currency)), Parameter.Of(TranslationParam.amount, NumberFormat.formatMoney(Utils.nullTo0(MaxExposure), Currency))) : NumberFormat.formatMoney(Exposure, Currency, 2)}`,
        percentage: isQuantumAccount ? Utils.greaterThen0(Exposure) && Utils.greaterThen0(MaxExposure) ? Exposure / MaxExposure * 100 : 0 : null,
        clazz: isQuantumAccount ? " quantum" : "",
        hidden: isFinancialSwiper && !isQuantumAccount,
        quickLook: Utils.isArrayNotEmpty(accountDetailsConfig) ? getDetailVisibility(TranslationKey.headerExposure) : null
    };

    const AccountBalance: AccountDetailRowMeta = {
        title: TranslationKey.headerBalance,
        description: Translations.text(TranslationKey.headerDescriptionBalance),
        value: NumberFormat.formatMoney(Balance, Currency, 2),
        hidden: isFinancialSwiper && !IsInvestAccount,
        quickLook: Utils.isArrayNotEmpty(accountDetailsConfig) ? getDetailVisibility(TranslationKey.headerBalance) : null
    };

    return isFinancialSwiper ? [
            AccountEquity,
            UnrealizedProfit,
            MarginLevel,
            AvailableMargin,
            AccountExposure,
            AccountBalance,
        ]
        : [
            {
                title: TranslationKey.headerAvailableLine,
                description: Translations.text(TranslationKey.headerDescriptionAvailableLine),
                value: `${NumberFormat.formatMoney(AvailableLine, Currency, 2)}`,
                hidden: IsInvestAccount || isQuantumAccount,
                quickLook: Utils.isArrayNotEmpty(accountDetailsConfig) ? getDetailVisibility(TranslationKey.headerAvailableLine) : null
            },
            UnrealizedProfit,
            {
                title: TranslationKey.headerRealizedProfit,
                description: Translations.text(TranslationKey.headerDescriptionRealizedProfit),
                value: (RealizedPL > 0 ? "+" : "") + NumberFormat.formatMoney(RealizedPL, Currency, 2),
                clazz: RealizedPL > 0 ? "up" : RealizedPL < 0 ? "down" : "",
                hidden: dynamicConfiguration.DisableRealizedPLDetails || isQuantumAccount,
                quickLook: Utils.isArrayNotEmpty(accountDetailsConfig) ? getDetailVisibility(TranslationKey.headerRealizedProfit) : null
            },
            AccountBalance,
            AvailableMargin,
            {
                title: TranslationKey.headerUsedMargin,
                description: Translations.text(TranslationKey[Utils.isNotNull(MaintenanceMargin) ? "headerDescriptionUsedMarginWithMaintenance" : "headerDescriptionUsedMargin"]),
                value: `${NumberFormat.formatMoney(UsedMargin, Currency, 2)}`,
                hidden: isQuantumAccount,
                quickLook: Utils.isArrayNotEmpty(accountDetailsConfig) ? getDetailVisibility(TranslationKey.headerUsedMargin) : null
            },
            {
                title: TranslationKey.headerMaintenanceMargin,
                description: Translations.text(TranslationKey.headerDescriptionUsedMargin),
                value: `${NumberFormat.formatMoney(MaintenanceMargin, Currency, 2)}`,
                hidden: Utils.isNull(MaintenanceMargin) || IsInvestAccount || isQuantumAccount,
                quickLook: Utils.isArrayNotEmpty(accountDetailsConfig) ? getDetailVisibility(TranslationKey.headerMaintenanceMargin) : null
            },
            AccountExposure,
            MarginLevel,
            AccountEquity,
            {
                title: TranslationKey.headerWithdrawableEquity,
                description: Translations.text(TranslationKey.headerDescriptionWithdrawableEquity),
                value: `${NumberFormat.formatMoney(WithdrawableEquity, Currency, 2)}`,
                hidden: !WithdrawableEquityEnabled,
                quickLook: Utils.isArrayNotEmpty(accountDetailsConfig) ? getDetailVisibility(TranslationKey.headerWithdrawableEquity) : null
            }
        ];
}

const saveAccountConfig = async(config: AccountDetailsConfig[], key: AccountConfigType) => {
    if (config) {
        const preference: Preference<{[key: string]: AccountDetailsConfig[]}> = Platform.reduxState<StoreState>().settings.preferences.get(StorageKey.AccountDetailsConfig);
        const accountConfig: {[key: string]: AccountDetailsConfig[]} = Utils.deepCopy(preference?.value || {});
        accountConfig[key] = config;
        const newPreference: Preference<{[key: string]: AccountDetailsConfig[]}> = Preference.Of(accountConfig);

        Platform.dispatch(SetClientPreference({
            key: StorageKey.AccountDetailsConfig,
            preference: newPreference
        }));
        Platform.dispatch(InsertClientPreference({
            key: StorageKey.AccountDetailsConfig,
            preference: newPreference
        }));

        Platform.storage().setItem(StorageKey.AccountDetailsConfig, JSON.stringify(accountConfig));
    }
}

export {
    AccountDetailRowMeta,
    getAccountDetailsMeta,
    getDefaultAccountConfig,
    AccountDetailsConfig,
    defaultAccountConfig,
    saveAccountConfig
}
